import * as Stimulus from "@hotwired/stimulus"

/**
 * A search box that collects picks as chips. See App.UI.Elements.ChipPicker.
 *
 * The chip and option markup comes from templates drawn by the server; this
 * controller clones them and fills in text, so it carries no classes of its own.
 *
 * Every change dispatches "chip-picker:change" with the picker's field name.
 * A picker whose "narrowBy" names that field clears itself, because its picks
 * were found by a search the change has just narrowed.
 */
export class ChipPickerController extends Stimulus.Controller {
    static get targets() {
        return [
            "chips",
            "query",
            "results",
            "chipTemplate",
            "optionTemplate",
            "emptyTemplate",
            "unavailableTemplate",
        ];
    }

    static get values() {
        return {
            url: String,
            queryParam: String,
            name: String,
            multiple: Boolean,
            narrowBy: Array,
            required: Boolean,
            requireNarrowing: Boolean,
        };
    }

    connect() {
        this.options = [];
        this.highlighted = -1;
        this.requestId = 0;
        this.refreshNarrowing();
        this.updateValidity();
    }

    disconnect() {
        clearTimeout(this.timer);
        this.requestId++;
    }

    // Clicking the box focuses the text input, so the whole box behaves as the
    // input it looks like. The chip's own remove button is left alone.
    focus(event) {
        if (!event.target.closest("button")) {
            this.queryTarget.focus();
        }
    }

    // The list scrolls, and a mousedown on its scrollbar would otherwise blur
    // the input and close the list mid-drag.
    keepOpen(event) {
        event.preventDefault();
    }

    open() {
        this.fetchOptions();
    }

    search() {
        clearTimeout(this.timer);
        // The visible options answer the text as it was, so they are dropped
        // before the new search runs. Otherwise Enter within the debounce
        // picks from a list the typing has already ruled out.
        this.close();
        this.timer = setTimeout(() => this.fetchOptions(), 200);
    }

    // Anything already in flight is abandoned, so a late answer cannot reopen
    // the list after a pick or an Escape.
    close() {
        clearTimeout(this.timer);
        this.requestId++;
        this.options = [];
        this.resultsTarget.replaceChildren();
        this.resultsTarget.hidden = true;
        this.queryTarget.setAttribute("aria-expanded", "false");
        this.queryTarget.removeAttribute("aria-activedescendant");
        this.highlighted = -1;
    }

    navigate(event) {
        switch (event.key) {
            case "ArrowDown":
                event.preventDefault();
                if (this.resultsTarget.hidden) { clearTimeout(this.timer); this.fetchOptions(); return; }
                this.highlight(Math.min(this.highlighted + 1, this.options.length - 1));
                break;
            case "ArrowUp":
                event.preventDefault();
                if (this.options.length === 0) { return; }
                this.highlight(Math.max(this.highlighted - 1, 0));
                break;
            case "Enter":
                // Enter picks rather than submitting the form half filled.
                event.preventDefault();
                if (!this.resultsTarget.hidden && this.options[this.highlighted]) {
                    this.add(this.options[this.highlighted]);
                }
                break;
            case "Escape":
                this.close();
                break;
            case "Backspace":
                if (this.queryTarget.value === "") {
                    const chips = this.chipElements();
                    if (chips.length > 0) { this.removeChip(chips[chips.length - 1]); }
                }
                break;
        }
    }

    // mousedown rather than click, so the pick lands before the input's blur
    // hides the list it was made from.
    choose(event) {
        event.preventDefault();
        const index = Number(event.currentTarget.dataset.index);
        if (this.options[index]) { this.add(this.options[index]); }
    }

    remove(event) {
        this.removeChip(event.currentTarget.closest("[data-chip-value]"));
        this.queryTarget.focus();
    }

    narrowed(event) {
        if (event.target === this.element) { return; }
        const sourceQuery = event.target.querySelector('[data-chip-picker-target="query"]');
        if ((sourceQuery?.form || event.target.closest("form")) !== this.formElement()) { return; }
        if (!this.narrowByValue.includes(event.detail.name)) { return; }
        this.close();
        this.queryTarget.value = "";
        this.chipElements().forEach($chip => $chip.remove());
        this.refreshNarrowing();
        this.changed();
    }

    async fetchOptions() {
        if (this.queryTarget.disabled) { return; }
        // Only the path is kept. The server guesses its own address from the
        // request, and behind a proxy it guesses http for an https page, which
        // the browser refuses to fetch.
        const url = new URL(new URL(this.urlValue, window.location.origin).pathname, window.location.origin);
        url.searchParams.set(this.queryParamValue, this.queryTarget.value);
        const form = this.formElement();
        if (form) {
            const data = new FormData(form);
            this.narrowByValue.forEach(name => {
                data.getAll(name).forEach(value => url.searchParams.append(name, value));
            });
        }

        // Answers can arrive out of order when typing quickly; only the latest
        // request may draw the list.
        const requestId = ++this.requestId;
        let options;
        try {
            const response = await fetch(url, { headers: { Accept: "application/json" } });
            if (!response.ok) { throw new Error(response.status); }
            options = await response.json();
        } catch {
            // A search that cannot answer, most often an expired session
            // answering with the login page, says so rather than looking empty.
            if (requestId === this.requestId) { this.drawUnavailable(); }
            return;
        }
        if (requestId !== this.requestId || document.activeElement !== this.queryTarget) { return; }

        const picked = new Set(this.pickedValues());
        this.options = options.filter(option => !picked.has(option.value));
        this.drawOptions();
    }

    drawOptions() {
        this.resultsTarget.replaceChildren();
        if (this.options.length === 0) {
            this.resultsTarget.append(this.emptyTemplateTarget.content.cloneNode(true));
        }
        this.options.forEach((option, index) => {
            const $option = this.optionTemplateTarget.content.firstElementChild.cloneNode(true);
            $option.id = `${this.resultsTarget.id}-${index}`;
            $option.dataset.index = index;
            $option.querySelector("[data-slot=label]").textContent = option.label;
            const $detail = $option.querySelector("[data-slot=detail]");
            if (option.detail) { $detail.textContent = option.detail; } else { $detail.remove(); }
            this.resultsTarget.append($option);
        });
        this.resultsTarget.hidden = false;
        this.queryTarget.setAttribute("aria-expanded", "true");
        this.highlight(this.options.length > 0 ? 0 : -1);
    }

    drawUnavailable() {
        this.options = [];
        this.resultsTarget.replaceChildren(this.unavailableTemplateTarget.content.cloneNode(true));
        this.resultsTarget.hidden = false;
        this.queryTarget.setAttribute("aria-expanded", "true");
        this.highlight(-1);
    }

    highlight(index) {
        this.highlighted = index;
        Array.from(this.resultsTarget.children).forEach(($option, i) => {
            $option.setAttribute("aria-selected", i === index ? "true" : "false");
            if (i === index) { $option.scrollIntoView({ block: "nearest" }); }
        });
        if (index >= 0) {
            this.queryTarget.setAttribute("aria-activedescendant", `${this.resultsTarget.id}-${index}`);
        } else {
            this.queryTarget.removeAttribute("aria-activedescendant");
        }
    }

    add(option) {
        if (!this.multipleValue) {
            this.chipElements().forEach($chip => $chip.remove());
        }
        const $chip = this.chipTemplateTarget.content.firstElementChild.cloneNode(true);
        $chip.dataset.chipValue = option.value;
        $chip.querySelector("[data-slot=label]").textContent = option.label;
        $chip.querySelector("[data-slot=label]").title = option.label;
        $chip.querySelector("input[type=hidden]").value = option.value;
        this.chipsTarget.append($chip);

        this.queryTarget.value = "";
        this.close();
        this.changed();
    }

    removeChip($chip) {
        if (!$chip) { return; }
        $chip.remove();
        this.changed();
    }

    changed() {
        this.updateValidity();
        this.queryTarget.dispatchEvent(new Event("change", { bubbles: true }));
        this.dispatch("change", { detail: { name: this.nameValue } });
    }

    formElement() {
        return this.queryTarget.form || this.element.closest("form");
    }

    refreshNarrowing() {
        if (!this.requireNarrowingValue) { return; }
        const form = this.formElement();
        const data = form ? new FormData(form) : null;
        this.queryTarget.disabled = !data || this.narrowByValue.some(name => !data.get(name));
        this.queryTarget.placeholder = this.queryTarget.disabled ? "Select an option above first" : "";
    }

    updateValidity() {
        if (!this.requiredValue) { return; }
        this.queryTarget.setCustomValidity(this.chipElements().length ? "" : "Select an option from the search results.");
    }

    validate(event) {
        this.updateValidity();
        if (!this.queryTarget.checkValidity()) {
            event.preventDefault();
            this.dispatch("invalid", {
                prefix: "form", target: this.queryTarget,
                detail: { errorMessage: this.queryTarget.validationMessage },
            });
        }
    }

    chipElements() {
        return Array.from(this.chipsTarget.querySelectorAll("[data-chip-value]"));
    }

    pickedValues() {
        return this.chipElements().map($chip => $chip.dataset.chipValue);
    }
}
